import { ControlType, type ActionTemplateParameterResource, type LibraryVariableSetResource, type ProjectResource, type PropertyValueResource, type ModifyTenantCommonVariableValue, type ModifyTenantProjectVariableValue, type TenantResource, type TenantVariableResource, } from "@octopusdeploy/octopus-server-client";
import { generateGuid } from "~/areas/projects/components/Process/generation";
import { repository } from "~/clientInstance";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
export interface TenantPageCommonVariableModel extends ModifyTenantCommonVariableValue {
    HasScopeChanged: boolean;
    HasValueChanged: boolean;
    Status: "overridden" | "default" | "missing" | "new";
}
export interface PageModel {
    variables: TenantVariableResource;
    commonVariables: TenantPageCommonVariableModel[];
    projectVariables: ModifyTenantProjectVariableValue[];
}
export interface InitialPageData extends PageModel {
    containsAccounts: boolean;
    projects?: ProjectResource[];
    libraryVariableSets?: LibraryVariableSetResource[];
}
const accountTypes: ControlType[] = [ControlType.AmazonWebServicesAccount, ControlType.AzureAccount, ControlType.GenericOidcAccount, ControlType.GoogleCloudAccount, ControlType.UsernamePasswordAccount];
function includesAccountTypes(variables: TenantVariableResource) {
    const libraryVariableHasAccounts = Object.keys(variables.LibraryVariables).some((libraryVariableSetId) => {
        const templates = variables.LibraryVariables[libraryVariableSetId].Templates;
        return templates.some((t) => accountTypes.includes(t.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText));
    });
    if (libraryVariableHasAccounts)
        return true;
    const projectHasAccounts = Object.keys(variables.ProjectVariables).some((projectId) => {
        const templates = variables.ProjectVariables[projectId].Templates;
        return templates.some((t) => accountTypes.includes(t.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText));
    });
    if (projectHasAccounts)
        return true;
    return false;
}
interface MissingCommonTemplateMap {
    [libraryVariableSetId: string]: ActionTemplateParameterResource[];
}
function getMissingCommonTemplateMap(tenant: TenantResource, libraryVariableSets: LibraryVariableSetResource[], variables: ModifyTenantCommonVariableValue[]): MissingCommonTemplateMap {
    const environmentIds = Object.keys(tenant.ProjectEnvironments).flatMap((projectId) => tenant.ProjectEnvironments[projectId]);
    const missingTemplates = libraryVariableSets.map((lvs) => {
        const missingTemplates = lvs.Templates.filter((t) => {
            if (variables.some((v) => v.LibraryVariableSetId === lvs.Id && v.TemplateId === t.Id && v.Scope.EnvironmentIds.length === 0))
                return false;
            return !environmentIds.every((eId) => variables.some((v) => v.LibraryVariableSetId === lvs.Id && v.TemplateId === t.Id && v.Scope.EnvironmentIds.includes(eId)));
        });
        return { libraryVariableSetId: lvs.Id, missingTemplates };
    });
    return missingTemplates.reduce((result, { libraryVariableSetId, missingTemplates }) => ({ ...result, [libraryVariableSetId]: missingTemplates }), {});
}
function generateVariable(libraryVariableSet: LibraryVariableSetResource, template: ActionTemplateParameterResource): TenantPageCommonVariableModel {
    return {
        Id: generateGuid(),
        LibraryVariableSetId: libraryVariableSet.Id,
        TemplateId: template.Id,
        Value: null,
        HasScopeChanged: false,
        HasValueChanged: false,
        Status: !template.DefaultValue ? "missing" : "default",
        Scope: {
            EnvironmentIds: [],
        },
    };
}
async function getModelFromApi(tenant: TenantResource): Promise<InitialPageData> {
    if (!isFeatureToggleEnabled("CommonVariableScopingFeatureToggle")) {
        const variables = await repository.Tenants.getVariables(tenant);
        return {
            variables,
            commonVariables: [],
            projectVariables: [],
            containsAccounts: includesAccountTypes(variables),
        };
    }
    // This is a temporary implementation to maintain the existing model structure
    const variables: TenantVariableResource = {
        Id: "",
        TenantId: tenant.Id,
        TenantName: tenant.Name,
        SpaceId: tenant.SpaceId,
        Links: {},
        LibraryVariables: {},
        ProjectVariables: {},
    };
    const projectVariables = (await repository.Tenants.getProjectVariablesByTenantId(tenant.Id)).Variables;
    const projectIds = Object.keys(tenant.ProjectEnvironments);
    const projects = await Promise.all(projectIds.map((projectId) => repository.Projects.get(projectId)));
    variables.ProjectVariables = projects.reduce((result, project) => ({
        ...result,
        [project.Id]: {
            ProjectId: project.Id,
            ProjectName: project.Name,
            Templates: project.Templates,
            Variables: tenant.ProjectEnvironments[project.Id].reduce((variables, envId) => ({ ...variables, [envId]: {} }), {}),
            TenantVariables: {},
            Links: project.Links,
        },
    }), {});
    projectVariables.forEach((projectVariable) => {
        const projectId = projectVariable.ProjectId;
        const environmentId = projectVariable.Scope.EnvironmentIds[0];
        const templateId = projectVariable.TemplateId;
        variables.ProjectVariables[projectId].Variables[environmentId] = {
            ...variables.ProjectVariables[projectId].Variables[environmentId],
            [templateId]: projectVariable.Value,
        };
    });
    const commonVariables: TenantPageCommonVariableModel[] = (await repository.Tenants.getCommonVariablesByTenantId(tenant.Id)).Variables.map((v) => ({
        ...v,
        HasScopeChanged: false,
        HasValueChanged: false,
        Status: "overridden",
    }));
    const libraryVariableSetIds = Array.from(new Set(projects.flatMap((project) => project.IncludedLibraryVariableSetIds)));
    const libraryVariableSets = await Promise.all(libraryVariableSetIds.map((libraryVariableSetId) => repository.LibraryVariableSets.get(libraryVariableSetId)));
    variables.LibraryVariables = {};
    const containsAccounts = libraryVariableSets.some((s) => s.Templates.some((t) => accountTypes.includes(t.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText))) ||
        projects.some((p) => p.Templates.some((t) => accountTypes.includes(t.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText)));
    const missingCommonTemplateMap = getMissingCommonTemplateMap(tenant, libraryVariableSets, commonVariables);
    libraryVariableSets.forEach((lvs) => {
        const missingTemplates = missingCommonTemplateMap[lvs.Id] ?? [];
        missingTemplates.forEach((template) => {
            commonVariables.push(generateVariable(lvs, template));
        });
    });
    return { variables, commonVariables, projectVariables, projects, libraryVariableSets, containsAccounts };
}
async function sendModelToApi(tenant: TenantResource, model: PageModel): Promise<void> {
    if (!isFeatureToggleEnabled("CommonVariableScopingFeatureToggle")) {
        await repository.Tenants.setVariables(tenant, model.variables);
        return;
    }
    await repository.Tenants.setProjectVariablesByTenantId(tenant.Id, {
        TenantId: tenant.Id,
        Variables: model.projectVariables,
    });
    await repository.Tenants.setCommonVariablesByTenantId(tenant.Id, {
        TenantId: tenant.Id,
        Variables: model.commonVariables.filter((v) => v.Value !== null),
    });
}
function updateModelWithCommonVariable(model: PageModel, libraryVariableSetId: string, templateId: string, value: PropertyValueResource): PageModel {
    return {
        ...model,
        variables: {
            ...model.variables,
            LibraryVariables: {
                ...model.variables.LibraryVariables,
                [libraryVariableSetId]: {
                    ...model.variables.LibraryVariables[libraryVariableSetId],
                    Variables: {
                        ...model.variables.LibraryVariables[libraryVariableSetId].Variables,
                        [templateId]: value,
                    },
                },
            },
        },
    };
}
export type ModelAction = UpdateValueModelAction | UpdateScopeModelAction | AddModelAction | RemoveModelAction;
type UpdateValueModelAction = {
    action: "updateValue";
    variableId: string;
    value: PropertyValueResource;
};
type UpdateScopeModelAction = {
    action: "updateScope";
    variableId: string;
    environmentIds: string[];
};
type AddModelAction = {
    action: "add";
    templateId: string;
    libraryVariableSetId: string;
};
type RemoveModelAction = {
    action: "remove";
    variableId: string;
};
function updateModelWithCommonVariableV2(model: PageModel, action: ModelAction): PageModel {
    switch (action.action) {
        case "updateValue":
            return updateCommonVariableModelValue(model, action.variableId, action.value);
        case "updateScope":
            return updateCommonVariableModelScope(model, action.variableId, action.environmentIds);
        case "add":
            return addCommonVariableModel(model, action.libraryVariableSetId, action.templateId);
        case "remove":
            return removeCommonVariableModel(model, action.variableId);
    }
}
function updateCommonVariableModelValue(model: PageModel, variableId: string, value: PropertyValueResource): PageModel {
    return {
        ...model,
        commonVariables: model.commonVariables.map((c) => (c.Id === variableId ? { ...c, Value: value, HasValueChanged: true } : c)),
    };
}
function updateCommonVariableModelScope(model: PageModel, variableId: string, environmentIds: string[]): PageModel {
    return {
        ...model,
        commonVariables: model.commonVariables.map((c) => (c.Id === variableId ? { ...c, Scope: { EnvironmentIds: environmentIds }, HasScopeChanged: true } : c)),
    };
}
function addCommonVariableModel(model: PageModel, libraryVariableSetId: string, templateId: string): PageModel {
    return {
        ...model,
        commonVariables: [
            ...model.commonVariables,
            {
                Id: generateGuid(),
                LibraryVariableSetId: libraryVariableSetId,
                TemplateId: templateId,
                Value: null,
                HasScopeChanged: true,
                HasValueChanged: true,
                Status: "new",
                Scope: {
                    EnvironmentIds: [],
                },
            },
        ],
    };
}
function removeCommonVariableModel(model: PageModel, variableId: string): PageModel {
    const removedVariable = model.commonVariables.find((c) => c.Id === variableId);
    if (removedVariable === undefined)
        return model;
    const variablesWithoutVariable = model.commonVariables.filter((c) => c.Id !== variableId);
    const hasRemaining = variablesWithoutVariable.some((c) => c.LibraryVariableSetId === removedVariable.LibraryVariableSetId && c.TemplateId === removedVariable.TemplateId);
    if (!hasRemaining) {
        variablesWithoutVariable.push({
            Id: generateGuid(),
            LibraryVariableSetId: removedVariable.LibraryVariableSetId,
            TemplateId: removedVariable.TemplateId,
            Value: null,
            HasScopeChanged: true,
            HasValueChanged: true,
            Status: "new",
            Scope: {
                EnvironmentIds: [],
            },
        });
    }
    return {
        ...model,
        commonVariables: variablesWithoutVariable,
    };
}
function updateModelWithProjectVariable(model: PageModel, projectId: string, environmentId: string, templateId: string, value: PropertyValueResource): PageModel {
    const newModel = {
        ...model,
        variables: {
            ...model.variables,
            ProjectVariables: {
                ...model.variables.ProjectVariables,
                [projectId]: {
                    ...model.variables.ProjectVariables[projectId],
                    Variables: {
                        ...model.variables.ProjectVariables[projectId].Variables,
                        [environmentId]: {
                            ...model.variables.ProjectVariables[projectId].Variables[environmentId],
                            [templateId]: value,
                        },
                    },
                },
            },
        },
    };
    if (!isFeatureToggleEnabled("CommonVariableScopingFeatureToggle"))
        return newModel;
    const index = newModel.projectVariables.findIndex((p) => p.ProjectId === projectId && p.Scope.EnvironmentIds.length === 1 && p.Scope.EnvironmentIds[0] === environmentId && p.TemplateId === templateId);
    if (index !== -1) {
        return {
            ...newModel,
            projectVariables: newModel.projectVariables.map((p, i) => (i === index ? { ...p, Value: value ?? "" } : p)),
        };
    }
    return {
        ...newModel,
        projectVariables: [
            ...newModel.projectVariables,
            {
                Id: undefined,
                ProjectId: projectId,
                TemplateId: templateId,
                Value: value ?? "",
                Scope: {
                    EnvironmentIds: [environmentId],
                },
            },
        ],
    };
}
export { getModelFromApi, sendModelToApi, updateModelWithCommonVariable, updateModelWithCommonVariableV2, updateModelWithProjectVariable };
